import * as Analytics from 'analytics/analytics'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'
import { rem } from 'polished'
import { memo, useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import getUSPContent from 'selectors/frontPage/getUSPContent'
import styled from 'styled-components'

const USPTile = styled(Group)`
  ${mediaQueryUp.tablet} {
    width: ${rem(296)};
  }

  ${mediaQueryUp.desktop} {
    width: unset;
  }
`

function FrontPageUSPComponent() {
  const intl = useIntl()
  const uspItems = useAppSelector(getUSPContent)
  const showModal = useContext(ModalContext)

  const isAboutUsPageAvailable = useMemo<boolean>(() => !!getStaticPagesByType(['aboutUs']).length, [])

  const handleLearnMoreClick = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'learn_about_us',
      action: 'clicked',
      category: 'usp_about_us',
      type: 'interaction',
    })
  }, [])

  const handleSignupClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const headingText = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'rdcMYMPvCGI_HgjU7TjF',
        defaultMessage: 'Exclusive prices and <italic>more</italic> with every booking',
        description: 'Front page USP section heading in the booking or order summary view',
      },
      {
        italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    )
  }, [intl])

  const learnMoreText = intl.formatMessage({
    id: '_avGsqleTmNtxMdcZ0yP',
    defaultMessage: 'Learn more about us',
    description: 'Learn more about us button text in the usp section',
  })

  const signUpText = intl.formatMessage({
    id: '0dj_CoePhvKb0DtHAkwf',
    defaultMessage: 'Sign up and save',
    description: 'Button text to sign up for an account and access exclusive member savings and deals',
  })

  return <VerticalSpacer gap={32}>
    <Heading variant="heading2">{headingText}</Heading>
    <CSSBreakpoint max="mobile">
      <Group gap={8} direction="vertical">
        {uspItems.map((item, index) => <BodyTextBlock
          key={index}
          variant="medium"
          colour="neutral-one"
          startIcon={<item.Icon />}
        >
          {intl.formatMessage(item.bodyMessage)}
        </BodyTextBlock>)}
      </Group>
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <CardCarousel pageSize={uspItems.length}>
        {uspItems.map((item, index) => <USPTile key={index} direction="vertical" gap={12}>
          <item.Icon size="L" />
          <BodyText variant="medium">{intl.formatMessage(item.bodyMessage)}</BodyText>
        </USPTile>)}
      </CardCarousel>
    </CSSBreakpoint>
    <Group
      gap={8}
      direction="vertical-reverse"
      tabletDirection="horizontal"
      tabletHorizontalAlign="end"
    >
      {isAboutUsPageAvailable && <TextButton kind="tertiary" onClick={handleLearnMoreClick} to="/about-us" target="_blank">
        {learnMoreText}
      </TextButton>}
      <TextButton kind="primary" onClick={handleSignupClick}>
        {signUpText}
      </TextButton>
    </Group>
  </VerticalSpacer>
}

export default memo(FrontPageUSPComponent)
